/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/

import React, { useState, useEffect, useCallback } from "react";
import {
    Dialog,
    DialogContent,
    DialogActions,
    Grid,
    Stack,
    Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Button from "components/generals/Button";
import CurrencyInput from "components/generals/CurrencyInput";
import Autocomplete from "components/generals/Autocomplete";
import Input from "components/generals/Input";
import Card from "components/generals/Card";
import Box from "components/generals/Box";
import { CurrencyEnum, ExchangeRateInterface } from "@malmur-technologies/medicalsystem-shared";
import { formatPrice, formatPriceInTheOtherCurrency } from "utils/number.utils";
import MDAlert from "dashboard-template/components/MDAlert";

/**
 * Properties for the ConfirmPaymentForm component.
 */
interface ConfirmPaymentFormProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (paymentAmount: number) => void;
    totalDebt: number;
    totalPaid: number;
    remainingBalance: number;
    amountToPay: number;
    officialCurrency: CurrencyEnum;
    paymentSelectedCurrency: CurrencyEnum;
    exchangeRate: ExchangeRateInterface | null;
}

/**
 * Confirmation dialog for payment processing.
 * 
 * Features:
 * - Display financial summary (debt, paid, balance)
 * - Input field for amount received from customer
 * - Shows amount to charge (from billing encounters)
 * - Calculates and displays change to return
 * - Currency selector for payment
 * - Confirm and cancel actions
 * 
 * @param props - Component properties
 * @returns Payment confirmation dialog
 */
const ConfirmPaymentForm: React.FC<ConfirmPaymentFormProps> = ({
    open,
    onClose,
    onConfirm,
    totalDebt,
    totalPaid,
    remainingBalance,
    amountToPay,
    officialCurrency,
    paymentSelectedCurrency,
    exchangeRate,
}) => {
    const { t } = useTranslation();
    const [receivedAmount, setReceivedAmount] = useState<number>(amountToPay);
    const [selectedCurrency, setSelectedCurrency] = useState<CurrencyEnum>(paymentSelectedCurrency);
    const [change, setChange] = useState<number>(0);
    const [hasFocused, setHasFocused] = useState(false);

    const currencyOptions = [
        { id: CurrencyEnum.USD, label: t("currency.usd") },
        { id: CurrencyEnum.MXN, label: t("currency.mxn") },
    ];

    // Currency conversion functions
    const convertMXNToUSD = useCallback(
        (amount: number) => {
            if (!exchangeRate) return amount;
            const rate = parseFloat(exchangeRate.rate.toString());
            return rate > 0 ? amount / rate : amount;
        },
        [exchangeRate]
    );

    const getValueInOfficialCurrency = useCallback(
        (amount: number, currency: CurrencyEnum) => {
            if (currency === officialCurrency) {
                return amount;
            } else {
                if (currency === CurrencyEnum.MXN && officialCurrency === CurrencyEnum.USD) {
                    return convertMXNToUSD(amount);
                } else if (currency === CurrencyEnum.USD && officialCurrency === CurrencyEnum.MXN) {
                    if (!exchangeRate) return amount;
                    const rate = parseFloat(exchangeRate.rate.toString());
                    return rate > 0 ? amount * rate : amount;
                }
                return amount;
            }
        },
        [convertMXNToUSD, exchangeRate, officialCurrency]
    );

    const convertFromOfficialCurrency = useCallback(
        (amount: number, targetCurrency: CurrencyEnum) => {
            if (targetCurrency === officialCurrency) {
                return amount;
            } else {
                if (officialCurrency === CurrencyEnum.USD && targetCurrency === CurrencyEnum.MXN) {
                    if (!exchangeRate) return amount;
                    const rate = parseFloat(exchangeRate.rate.toString());
                    return rate > 0 ? amount * rate : amount;
                } else if (officialCurrency === CurrencyEnum.MXN && targetCurrency === CurrencyEnum.USD) {
                    return convertMXNToUSD(amount);
                }
                return amount;
            }
        },
        [officialCurrency, exchangeRate, convertMXNToUSD]
    );

    // Calculate change when received amount changes
    useEffect(() => {
        // amountToPay is already in official currency, convert it to selected currency
        const amountToPayInSelectedCurrency = convertFromOfficialCurrency(amountToPay, selectedCurrency);
        const changeAmount = receivedAmount - amountToPayInSelectedCurrency;
        setChange(changeAmount >= 0 ? changeAmount : 0);
    }, [receivedAmount, amountToPay, selectedCurrency, convertFromOfficialCurrency]);

    // Update selected currency when paymentSelectedCurrency changes
    useEffect(() => {
        setSelectedCurrency(paymentSelectedCurrency);
    }, [paymentSelectedCurrency]);

    // Reset form when dialog opens/closes
    useEffect(() => {
        if (open) {
            // IMPORTANT: Use paymentSelectedCurrency directly to ensure we use the correct currency from PaymentForm
            setSelectedCurrency(paymentSelectedCurrency);
            const amountToPayInSelectedCurrency = convertFromOfficialCurrency(amountToPay, paymentSelectedCurrency);
            setReceivedAmount(amountToPayInSelectedCurrency);
            setHasFocused(false);
        }
    }, [open, amountToPay, paymentSelectedCurrency, convertFromOfficialCurrency]);

    const handleConfirm = () => {
        onConfirm(amountToPay);
    };

    // amountToPay is already in official currency
    const amountToPayInSelectedCurrency = convertFromOfficialCurrency(amountToPay, selectedCurrency);
    const isInsufficientAmount = receivedAmount < amountToPayInSelectedCurrency;
    const changeInOfficialCurrency = selectedCurrency !== officialCurrency ? getValueInOfficialCurrency(change, selectedCurrency) : null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            className="confirm-payment-form__dialog"
        >
            <DialogContent className="confirm-payment-form__content">
                <Stack spacing={2} sx={{ mt: 0 }}>
                    <Box className="confirm-payment-form__header">
                        <Typography variant="h5" className="confirm-payment-form__header-title">
                            {t("confirmPaymentForm.title")}
                        </Typography>
                        <Typography variant="subtitle2" className="confirm-payment-form__header-subtitle">
                            {t("paymentForm.financialSummary")} - {t("paymentForm.officialCurrency")} {officialCurrency}
                        </Typography>
                    </Box>

                    {/* Financial Summary */}
                    <Card className="card-base confirm-payment-form__financial-summary">
                        <Box p={2}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <Input
                                        label={t("paymentForm.totalDebt")}
                                        value={formatPrice(totalDebt)}
                                        disabled
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box>
                                        <Input
                                            label={t("paymentForm.totalToPayCurrentPayment")}
                                            value={formatPrice(amountToPay)}
                                            disabled
                                            fullWidth
                                            className="confirm-payment-form__current-payment-input"
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Input
                                        label={t("paymentForm.remainingBalance")}
                                        value={formatPrice(totalDebt - (amountToPay))}
                                        disabled
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Card>

                    {/* Payment Details */}
                    <Grid container spacing={2} className="confirm-payment-form__payment-details">
                        <Grid item xs={12} md={5} className="confirm-payment-form__currency-selector">
                            <Autocomplete
                                options={currencyOptions}
                                name="currency"
                                label={t("confirmPaymentForm.paymentCurrency")}
                                value={selectedCurrency}
                                defaultValue={currencyOptions.find((option) => option.id === paymentSelectedCurrency)}
                                onChange={(event, selectedOption) => {
                                    setSelectedCurrency((selectedOption?.id as CurrencyEnum) || officialCurrency);
                                }}
                                noOptionsText={t("paymentForm.noOptions")}
                                formikOutside={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={6} className="confirm-payment-form__received-amount">
                            <Box>
                                <CurrencyInput
                                    label={t("confirmPaymentForm.receivedAmount")}
                                    value={receivedAmount}
                                    onChange={setReceivedAmount}
                                    currency={selectedCurrency}
                                    showCurrencySymbol={true}
                                    fullWidth
                                    minValue={0}
                                    maxValue={999999999.99}
                                    onFocus={() => {
                                        if (!hasFocused) {
                                            setReceivedAmount(0);
                                            setHasFocused(true);
                                        }
                                    }}
                                />
                                {selectedCurrency !== officialCurrency && receivedAmount > 0 && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mt: 0.5, display: "block" }}
                                    >
                                        ≈ {formatPrice(getValueInOfficialCurrency(receivedAmount, selectedCurrency))}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6} className="confirm-payment-form__change">
                            <Box>
                                <Input
                                    label={t("confirmPaymentForm.change")}
                                    value={selectedCurrency === officialCurrency ? formatPrice(change) : formatPriceInTheOtherCurrency(change)}
                                    disabled
                                    fullWidth
                                />
                                {selectedCurrency !== officialCurrency && change > 0 && changeInOfficialCurrency !== null && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mt: 0.5, display: "block" }}
                                    >
                                        (≈ {formatPrice(changeInOfficialCurrency)})
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Stack>
                {isInsufficientAmount && (
                    <Grid item xs={12} md={12}>
                        <MDAlert severity="error" color="error">
                            {t("confirmPaymentForm.insufficientAmount")}
                        </MDAlert>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions className="confirm-payment-form__actions">
                <Button
                    onClick={onClose}
                    color="secondary"
                    variant="contained"
                    className="confirm-payment-form__cancel-button"
                >
                    {t("common.cancel")}
                </Button>
                <Button
                    id="cy-confirmPayment-button"
                    onClick={handleConfirm}
                    color="success"
                    variant="contained"
                    disabled={isInsufficientAmount}
                    className="confirm-payment-form__confirm-button"
                >
                    {t("confirmPaymentForm.confirmPayment")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmPaymentForm;
